'use client';

import type React from 'react';
import {
  type FC,
  memo,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ButtonClose } from '@/components/buttons';
import { buildTestId, type WithTestId } from '@/helpers/testIds';
import { useScrollBlock } from '@/modules/common/hooks/useScrollBlock';
import type { RegistrationConfig } from '@/modules/registration/helpers/types';
import { ImageComponent } from '../Image';

import {
  ScModalSelect,
  ScModalSelectBody,
  type ScModalSelectBodyProps,
  ScModalSelectFooter,
  ScModalSelectHeader,
  ScModalSelectOption,
  type ScModalSelectProps,
  ScModalSelectRow,
  ScSelectSearchMob,
} from './styled';

type Option = {
  icon?: string;
  name: React.ReactNode;
  nameS?: string;
  nameV?: string;
  value: string | number;
  disabled?: boolean;
  isActive?: boolean;
  styleScheme?: RegistrationConfig['styleScheme'];
};

interface ModalSelectProps
  extends ScModalSelectProps,
    ScModalSelectBodyProps,
    WithTestId {
  title: string;
  icon?: JSX.Element;
  options: Omit<Option, 'isActive'>[];
  borderRoundIconOption?: boolean;
  onClose: () => void;
  onChange?: (value: Option['value']) => void;
  search?: boolean;
  cols?: number;
  rowGap?: number;
  openOptionsInPortal?: boolean;
  addons?: JSX.Element;
  isPortal?: boolean;
  activeOption?: Option;
}

export const ModalSelect: FC<ModalSelectProps> = ({
  options,
  borderRoundIconOption = false,
  onChange,
  search,
  cols = 2,
  rowGap = 8,
  addons,
  isPortal,
  activeOption,
  testId,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');

  const handleChangeSearchValue = useCallback(
    (value: string) => {
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const filteredOptions = useMemo(() => {
    if (searchValue === '' || search == null) return options;

    return options.filter(
      ({ nameS }) =>
        !nameS || nameS.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue, options, search]);

  const component = useMemo(
    () => (
      <ModalSelectContainer
        onChangeSearch={handleChangeSearchValue}
        search={search}
        testId={testId}
        {...props}
      >
        {filteredOptions.length === 0 && (
          <div className="not-found">Not found</div>
        )}
        <ScModalSelectRow cols={cols} rowGap={rowGap}>
          {filteredOptions.map((option, index) => (
            <OptionItem
              key={index}
              {...option}
              borderRoundIconOption={borderRoundIconOption}
              isActive={activeOption?.value === option.value}
              onChange={onChange}
              parentTestId={testId}
              styleScheme={props.styleScheme}
            />
          ))}
        </ScModalSelectRow>
        {addons}
      </ModalSelectContainer>
    ),
    [
      handleChangeSearchValue,
      search,
      props,
      filteredOptions,
      cols,
      rowGap,
      addons,
      borderRoundIconOption,
      onChange,
      activeOption?.value,
    ]
  );

  return isPortal ? createPortal(component, document.body) : component;
};

interface ModalSelectContainerProps
  extends PropsWithChildren,
    ScModalSelectProps,
    ScModalSelectBodyProps,
    WithTestId {
  title: string;
  icon?: JSX.Element;
  search?: boolean;
  bodyStyle?: React.CSSProperties;
  addons?: JSX.Element;
  onClose: () => void;
  onChangeSearch?: (value: string) => void;
  openOptionsInPortal?: boolean;
}

export const ModalSelectContainer: FC<ModalSelectContainerProps> = ({
  children,
  title,
  icon,
  search,
  addons,
  onClose,
  onChangeSearch,
  innerAlignBottom,
  bodyStyle,
  openOptionsInPortal,
  isEstonia,
  testId,
  ...props
}) => {
  useScrollBlock(props?.isHidden);

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current == null || search == null) return;

    ref.current.focus();
  }, [search]);

  const handleChangeSearchValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChangeSearch && onChangeSearch(e.target.value);
    },
    [onChangeSearch]
  );

  const modalSelectComponent = useMemo(
    () => (
      <ScModalSelect {...props} data-testid={testId}>
        <ButtonClose
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          right={16}
          top={16}
        />
        <ScModalSelectHeader {...props}>
          <div className="header">
            {icon && <div className="icon">{icon}</div>}
            <div className="title">{title}</div>
          </div>
        </ScModalSelectHeader>
        {search && (
          <ScSelectSearchMob {...props}>
            <input
              data-testid={buildTestId(testId, 'search')}
              onChange={handleChangeSearchValue}
              placeholder="Search..."
              ref={ref}
              type="text"
            />
          </ScSelectSearchMob>
        )}
        <ScModalSelectBody
          innerAlignBottom={innerAlignBottom}
          isEstonia={isEstonia}
          style={bodyStyle}
        >
          {children}
        </ScModalSelectBody>
        {addons && (
          <ScModalSelectFooter innerAlignBottom={innerAlignBottom}>
            {addons}
          </ScModalSelectFooter>
        )}
      </ScModalSelect>
    ),
    [
      props,
      icon,
      title,
      search,
      handleChangeSearchValue,
      innerAlignBottom,
      bodyStyle,
      isEstonia,
      children,
      addons,
      onClose,
    ]
  );

  return openOptionsInPortal
    ? createPortal(modalSelectComponent, document.body)
    : modalSelectComponent;
};

const OptionItem: FC<
  Pick<ModalSelectProps, 'borderRoundIconOption' | 'onChange'> &
    Option & { parentTestId?: string }
> = memo(
  ({
    name,
    value,
    icon,
    borderRoundIconOption,
    onChange,
    isActive,
    styleScheme,
    parentTestId,
  }) => {
    const handleClick = () => {
      if (onChange == null) return;
      onChange(value);
    };

    return (
      <ScModalSelectOption
        data-testid={buildTestId(parentTestId, 'option', value)}
        isActive={!!isActive}
        onClick={handleClick}
        roundIcon={borderRoundIconOption}
        styleScheme={styleScheme}
      >
        {icon && (
          <div className="icon">
            <ImageComponent
              alt={`${value}`}
              height={22}
              src={icon}
              width={22}
            />
          </div>
        )}
        <div className="name">{name}</div>
        {isActive && <div className="tick-circle-icon" />}
      </ScModalSelectOption>
    );
  }
);
